import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import styled, { createGlobalStyle } from "styled-components/macro";

const TARGET_SELECTOR = "[data-app-home-logo='true']";
const TRANSITION_MS = 720;
const OVERLAY_FADE_MS = 220;
const LOGO_SWAP_MS = 120;
const TARGET_WAIT_MS = 1600;
const TARGET_STABLE_FRAMES = 6;
const TARGET_STABLE_WAIT_MS = 600;
const SVG_DURATION_SECONDS = 5.6;
const ANIMATED_LOGO_DURATION_MS = SVG_DURATION_SECONDS * 1000;
const SVG_WIGGLE_START_SECONDS = SVG_DURATION_SECONDS * 0.675595;
const FAST_FORWARD_TO_WIGGLE_MS = 700;
const WIGGLE_PLAYBACK_MS = 1400;
const APP_LOGO_ANIMATION_SEEN_VERSION = "v1";
const APP_LOGO_ANIMATION_SKIP_GRACE_MS = 450;
const APP_LOGO_ANIMATION_MODE_SHOW = "show";
const APP_LOGO_ANIMATION_MODE_WAITING_TO_SKIP = "waiting-to-skip";
const APP_LOGO_ANIMATION_MODE_SKIPPED = "skipped";

type TransitionPhase = "loading" | "flying" | "fading" | "done";
type LogoAnimationMode =
  | typeof APP_LOGO_ANIMATION_MODE_SHOW
  | typeof APP_LOGO_ANIMATION_MODE_WAITING_TO_SKIP
  | typeof APP_LOGO_ANIMATION_MODE_SKIPPED;

interface UseAppLoadingLogoTransitionOptions {
  userId?: string | null;
  isAppReady: boolean;
  isAppMountBlocked?: boolean;
}

function getAppLogoAnimationSeenKey(userId: string) {
  return `sudowrite-logo-animation-seen:${APP_LOGO_ANIMATION_SEEN_VERSION}:${userId}`;
}

function hasSeenAppLogoAnimation(userId: string) {
  try {
    return window.localStorage.getItem(getAppLogoAnimationSeenKey(userId)) === "true";
  } catch (_error) {
    return false;
  }
}

function markAppLogoAnimationSeen(userId: string) {
  try {
    window.localStorage.setItem(getAppLogoAnimationSeenKey(userId), "true");
  } catch (_error) {
    // Ignore localStorage failures; they should not block app loading.
  }
}

function getInitialLogoAnimationMode(userId?: string | null): LogoAnimationMode {
  return userId && hasSeenAppLogoAnimation(userId) ? APP_LOGO_ANIMATION_MODE_WAITING_TO_SKIP : APP_LOGO_ANIMATION_MODE_SHOW;
}

export function useAppLoadingLogoTransition({ userId, isAppReady, isAppMountBlocked = false }: UseAppLoadingLogoTransitionOptions) {
  const [logoAnimationState, setLogoAnimationState] = useState(() => ({
    userId: userId || null,
    mode: getInitialLogoAnimationMode(userId),
  }));
  const [isTransitionActive, setIsTransitionActive] = useState(() => getInitialLogoAnimationMode(userId) === APP_LOGO_ANIMATION_MODE_SHOW);

  useEffect(() => {
    if (!userId) return;

    const mode = getInitialLogoAnimationMode(userId);
    setLogoAnimationState({
      userId,
      mode,
    });
    setIsTransitionActive(mode === APP_LOGO_ANIMATION_MODE_SHOW);
  }, [userId]);

  const logoAnimationMode =
    userId && logoAnimationState.userId !== userId ? getInitialLogoAnimationMode(userId) : logoAnimationState.mode;

  const shouldShowTransition = logoAnimationMode === APP_LOGO_ANIMATION_MODE_SHOW;

  useEffect(() => {
    if (!userId || logoAnimationMode !== APP_LOGO_ANIMATION_MODE_WAITING_TO_SKIP) return;

    if (isAppMountBlocked) {
      setLogoAnimationState({
        userId,
        mode: APP_LOGO_ANIMATION_MODE_SHOW,
      });
      setIsTransitionActive(true);
      return;
    }

    if (isAppReady) {
      setLogoAnimationState({
        userId,
        mode: APP_LOGO_ANIMATION_MODE_SKIPPED,
      });
      setIsTransitionActive(false);
      return;
    }

    const timeout = setTimeout(() => {
      setLogoAnimationState({
        userId,
        mode: APP_LOGO_ANIMATION_MODE_SHOW,
      });
      setIsTransitionActive(true);
    }, APP_LOGO_ANIMATION_SKIP_GRACE_MS);

    return () => clearTimeout(timeout);
  }, [isAppMountBlocked, isAppReady, logoAnimationMode, userId]);

  const handleTransitionComplete = useCallback(() => {
    if (!userId) return;

    markAppLogoAnimationSeen(userId);
    setLogoAnimationState({
      userId,
      mode: APP_LOGO_ANIMATION_MODE_SKIPPED,
    });
    setIsTransitionActive(false);
  }, [userId]);

  return {
    shouldShowTransition,
    isTransitionActive,
    isAppContentWaitingForTransition: shouldShowTransition && isTransitionActive,
    handleTransitionActiveChange: setIsTransitionActive,
    handleTransitionComplete,
  };
}

interface AppLoadingLogoTransitionProps {
  isDarkTheme?: boolean;
  isAppReady: boolean;
  onActiveChange?: (isActive: boolean) => void;
  onComplete?: () => void;
}

// AppHome pins the destination logo to `opacity: 1 !important` with a
// (0,3,1)-specificity selector, so this backstop rule stacks the attribute to
// outrank it. The primary hiding mechanism is an inline !important style set
// on the element itself (see hideDestinationTarget), which no stylesheet rule
// can override.
const HideDestinationLogo = createGlobalStyle`
  body.sudowrite-app-logo-transition-active ${TARGET_SELECTOR}${TARGET_SELECTOR}${TARGET_SELECTOR} {
    opacity: 0 !important;
  }
`;

const Overlay = styled.div<{ $phase: TransitionPhase }>`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  opacity: ${({ $phase }) => ($phase === "fading" ? 0 : 1)};
  transition: opacity ${OVERLAY_FADE_MS}ms ease-out;
`;

const FlightGroup = styled.span<{ $transform: string }>`
  position: relative;
  display: block;
  transform: ${({ $transform }) => $transform};
  transform-origin: center;
  transition: transform ${TRANSITION_MS}ms cubic-bezier(0.45, 0, 0.15, 1);
  will-change: transform;
`;

const AnimatedLogo = styled.object<{ $isDarkTheme: boolean; $isVisible: boolean }>`
  display: block;
  width: clamp(260px, 32vw, 384px);
  height: auto;
  aspect-ratio: 640 / 190;
  opacity: ${({ $isDarkTheme, $isVisible }) => {
    if (!$isVisible) return 0;
    return $isDarkTheme ? 0.95 : 1;
  }};
  filter: ${({ $isDarkTheme }) => ($isDarkTheme ? "brightness(0) invert(1)" : "none")};
  transition: opacity ${LOGO_SWAP_MS}ms ease-out;
`;

const FlightLogoFrame = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FlightLogo = styled.span<{ $isVisible: boolean }>`
  display: inline-block;
  width: clamp(188px, 23.15vw, 278px);
  aspect-ratio: 463 / 74;
  color: var(--grey-0, #383838);
  background-color: currentColor;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity ${LOGO_SWAP_MS}ms ease-out;
  -webkit-mask-image: url("/assets/sudowrite-logo.svg");
  mask-image: url("/assets/sudowrite-logo.svg");
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
`;

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function easeOutCubic(value: number) {
  return 1 - Math.pow(1 - value, 3);
}

function getRectKey(rect: DOMRect) {
  return [rect.left, rect.top, rect.width, rect.height].map((value) => Math.round(value * 2) / 2).join(",");
}

function computeFlightTransform(sourceRect: DOMRect, targetRect: DOMRect) {
  const scaleX = targetRect.width / sourceRect.width;
  const scaleY = targetRect.height / sourceRect.height;
  const translateX = targetRect.left + targetRect.width / 2 - (sourceRect.left + sourceRect.width / 2);
  const translateY = targetRect.top + targetRect.height / 2 - (sourceRect.top + sourceRect.height / 2);
  return `translate3d(${translateX}px, ${translateY}px, 0) scale(${scaleX}, ${scaleY})`;
}

function AppLoadingLogoTransition({
  isDarkTheme = false,
  isAppReady,
  onActiveChange,
  onComplete,
}: AppLoadingLogoTransitionProps) {
  const animatedLogoRef = useRef<HTMLObjectElement | null>(null);
  const logoRef = useRef<HTMLSpanElement | null>(null);
  const playbackRafRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const waitStartedAtRef = useRef<number | null>(null);
  const pendingTargetKeyRef = useRef<string | null>(null);
  const stableFrameCountRef = useRef(0);
  const hiddenTargetRef = useRef<HTMLElement | null>(null);
  const currentSvgTimeRef = useRef(0);
  const isAppReadyRef = useRef(isAppReady);
  const phaseRef = useRef<TransitionPhase>("loading");
  const isAnimatedLogoLoadedRef = useRef(false);
  const hasCompletedRef = useRef(false);
  const [animatedLogoSrc] = useState(() => `/assets/sudowrite-logo-animated.svg?restart=${Date.now()}`);
  const [isAnimatedLogoLoaded, setIsAnimatedLogoLoaded] = useState(false);
  const [isFallbackLogoCommitted, setIsFallbackLogoCommitted] = useState(false);
  const [animationDoneAt, setAnimationDoneAt] = useState<number | null>(null);
  const [isAnimationDone, setIsAnimationDone] = useState(false);
  const [isFlightComplete, setIsFlightComplete] = useState(false);
  const [phase, setPhase] = useState<TransitionPhase>("loading");
  const [transform, setTransform] = useState("translate3d(0, 0, 0) scale(1)");

  useEffect(() => {
    isAppReadyRef.current = isAppReady;
  }, [isAppReady]);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const handleAnimatedLogoError = useCallback(() => {
    // The animated SVG failed to load; show the static logo instead of the
    // broken <object>. Committing is one-way so a later load cannot swap the
    // visible logo back.
    setIsFallbackLogoCommitted(true);
    setAnimationDoneAt((current) => (current === null ? performance.now() : current));
  }, []);

  const attachAnimatedLogoRef = useCallback(
    (node: HTMLObjectElement | null) => {
      // React only attaches `load` (not `error`) listeners to <object>
      // elements, so an onError prop would silently never fire; listen for
      // the error event natively instead.
      animatedLogoRef.current?.removeEventListener("error", handleAnimatedLogoError);
      animatedLogoRef.current = node;
      node?.addEventListener("error", handleAnimatedLogoError);
    },
    [handleAnimatedLogoError],
  );

  const hideDestinationTarget = useCallback((target: HTMLElement) => {
    // AppHome forces the logo visible with `opacity: 1 !important`; an inline
    // !important declaration beats any stylesheet rule regardless of selector
    // specificity, so this is the reliable way to keep the destination hidden
    // while the overlay logo is on screen.
    target.style.setProperty("opacity", "0", "important");
    hiddenTargetRef.current = target;
  }, []);

  const revealDestinationTarget = useCallback(() => {
    document.body.classList.remove("sudowrite-app-logo-transition-active"); // pragma: allowlist secret
    hiddenTargetRef.current?.style.removeProperty("opacity");
    hiddenTargetRef.current = null;
  }, []);

  useEffect(() => {
    // Keep the destination logo hidden while the overlay logo is flying so the
    // two logos are never visible at once; it is revealed underneath the
    // overlay when the wiggle ends and the overlay fades out on top of it.
    document.body.classList.add("sudowrite-app-logo-transition-active");

    return () => {
      document.body.classList.remove("sudowrite-app-logo-transition-active");
      hiddenTargetRef.current?.style.removeProperty("opacity");
      hiddenTargetRef.current = null;
      if (playbackRafRef.current) window.cancelAnimationFrame(playbackRafRef.current);
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    // App content is revealed when the flight starts. The "fading" phase only
    // happens on the missing-target fallback, where there is no flight; keep
    // the content hidden until that fade finishes so a late-appearing header
    // logo can never show next to the still-fading overlay logo.
    onActiveChange?.(phase === "loading" || phase === "fading");
  }, [onActiveChange, phase]);

  useLayoutEffect(() => {
    if (phase !== "done" || hasCompletedRef.current) return;

    hasCompletedRef.current = true;
    revealDestinationTarget();
    onComplete?.();
  }, [onComplete, phase, revealDestinationTarget]);

  useEffect(() => {
    if (animationDoneAt === null || isAnimationDone) return;

    const delay = Math.max(animationDoneAt - performance.now(), 0);
    const timeout = window.setTimeout(() => setIsAnimationDone(true), delay);

    return () => window.clearTimeout(timeout);
  }, [animationDoneAt, isAnimationDone]);

  useEffect(() => {
    // Deliberately not keyed on `phase`: re-running this effect when the
    // flight starts would cancel and re-clamp an in-progress fast-forward or
    // wiggle segment, stretching its remaining frames into a visible hitch.
    // Ticks check phaseRef instead and stop once the overlay is going away.
    if (!isAnimatedLogoLoaded || animationDoneAt !== null || (phaseRef.current !== "loading" && phaseRef.current !== "flying")) return;

    const isOverlayLive = () => phaseRef.current === "loading" || phaseRef.current === "flying";

    const setSvgTime = (time: number) => {
      const objectElement = animatedLogoRef.current;
      const svg = objectElement?.contentDocument?.documentElement as
        | (SVGSVGElement & {
            pauseAnimations?: () => void;
            setCurrentTime?: (time: number) => void;
          })
        | undefined;

      if (!svg || typeof svg.setCurrentTime !== "function") return false;

      if (typeof svg.pauseAnimations === "function") svg.pauseAnimations();
      svg.setCurrentTime(time);
      currentSvgTimeRef.current = time;
      return true;
    };

    const clearPlaybackFrame = () => {
      if (playbackRafRef.current) {
        window.cancelAnimationFrame(playbackRafRef.current);
        playbackRafRef.current = null;
      }
    };

    const markAnimationDone = () => {
      setSvgTime(SVG_DURATION_SECONDS);
      setAnimationDoneAt(performance.now());
    };

    const playSegment = (from: number, to: number, durationMs: number, easing: (value: number) => number, onComplete: () => void) => {
      const segmentStartedAt = performance.now();

      const tick = () => {
        if (!isOverlayLive()) {
          playbackRafRef.current = null;
          return;
        }

        const progress = clamp((performance.now() - segmentStartedAt) / durationMs, 0, 1);
        const nextTime = from + (to - from) * easing(progress);
        setSvgTime(nextTime);

        if (progress < 1) {
          playbackRafRef.current = window.requestAnimationFrame(tick);
          return;
        }

        playbackRafRef.current = null;
        onComplete();
      };

      clearPlaybackFrame();
      tick();
    };

    const playReadySequence = () => {
      const currentTime = currentSvgTimeRef.current;

      if (currentTime >= SVG_DURATION_SECONDS) {
        markAnimationDone();
        return;
      }

      const playWiggle = () => {
        const wiggleFrom = Math.max(currentSvgTimeRef.current, SVG_WIGGLE_START_SECONDS);
        const remainingWiggleRatio = (SVG_DURATION_SECONDS - wiggleFrom) / (SVG_DURATION_SECONDS - SVG_WIGGLE_START_SECONDS);
        const wiggleDurationMs = clamp(WIGGLE_PLAYBACK_MS * remainingWiggleRatio, 650, WIGGLE_PLAYBACK_MS);
        playSegment(wiggleFrom, SVG_DURATION_SECONDS, wiggleDurationMs, (value) => value, markAnimationDone);
      };

      if (currentTime < SVG_WIGGLE_START_SECONDS) {
        const remainingFastForwardRatio = (SVG_WIGGLE_START_SECONDS - currentTime) / SVG_WIGGLE_START_SECONDS;
        const fastForwardDurationMs = clamp(FAST_FORWARD_TO_WIGGLE_MS * remainingFastForwardRatio, 260, FAST_FORWARD_TO_WIGGLE_MS);
        playSegment(currentTime, SVG_WIGGLE_START_SECONDS, fastForwardDurationMs, easeOutCubic, playWiggle);
        return;
      }

      playWiggle();
    };

    const controlsStartedAt = performance.now();
    const startPlayback = () => {
      if (!isOverlayLive()) {
        playbackRafRef.current = null;
        return;
      }

      if (!setSvgTime(currentSvgTimeRef.current)) {
        if (performance.now() - controlsStartedAt < 500) {
          playbackRafRef.current = window.requestAnimationFrame(startPlayback);
          return;
        }

        setAnimationDoneAt(performance.now() + ANIMATED_LOGO_DURATION_MS);
        return;
      }

      const naturalPlaybackStartedAt = performance.now() - currentSvgTimeRef.current * 1000;

      const playNaturallyUntilReady = () => {
        if (!isOverlayLive()) {
          playbackRafRef.current = null;
          return;
        }

        if (isAppReadyRef.current) {
          playReadySequence();
          return;
        }

        const nextTime = Math.min((performance.now() - naturalPlaybackStartedAt) / 1000, SVG_DURATION_SECONDS);
        setSvgTime(nextTime);

        if (nextTime >= SVG_DURATION_SECONDS) {
          markAnimationDone();
          return;
        }

        playbackRafRef.current = window.requestAnimationFrame(playNaturallyUntilReady);
      };

      playbackRafRef.current = window.requestAnimationFrame(playNaturallyUntilReady);
    };

    playbackRafRef.current = window.requestAnimationFrame(startPlayback);

    return clearPlaybackFrame;
  }, [animationDoneAt, isAnimatedLogoLoaded]);

  useEffect(() => {
    if (!isAppReady || phase !== "loading") return;

    if (prefersReducedMotion()) {
      setPhase("done");
      return;
    }

    waitStartedAtRef.current = performance.now();

    const tryStartFlight = () => {
      const source = logoRef.current;
      const target = document.querySelector<HTMLElement>(TARGET_SELECTOR);
      const elapsed = performance.now() - (waitStartedAtRef.current || performance.now());

      if (!source || !target) {
        if (elapsed > TARGET_WAIT_MS) {
          setPhase("fading");
          timeoutRef.current = window.setTimeout(() => setPhase("done"), OVERLAY_FADE_MS);
          return;
        }

        rafRef.current = window.requestAnimationFrame(tryStartFlight);
        return;
      }

      // The app content is still settling in its first frames (scrollbars,
      // banners, fonts, mount animations), which moves the destination.
      // Measure only once the rect has held still for a few frames so the
      // single flight lands where the logo will actually stay.
      const targetRect = target.getBoundingClientRect();
      const targetKey = getRectKey(targetRect);
      stableFrameCountRef.current = targetKey === pendingTargetKeyRef.current ? stableFrameCountRef.current + 1 : 0;
      pendingTargetKeyRef.current = targetKey;

      if (stableFrameCountRef.current < TARGET_STABLE_FRAMES && elapsed < TARGET_STABLE_WAIT_MS) {
        rafRef.current = window.requestAnimationFrame(tryStartFlight);
        return;
      }

      const sourceRect = source.getBoundingClientRect();
      hideDestinationTarget(target);

      if (!isAnimatedLogoLoadedRef.current) {
        // The animated SVG never arrived; fly the static logo instead of a
        // blank frame. Committing is one-way so a late SVG load cannot swap
        // the visible logo back mid-flight.
        setIsFallbackLogoCommitted(true);
        setAnimationDoneAt((current) => (current === null ? performance.now() : current));
      }

      setPhase("flying");
      setTransform(computeFlightTransform(sourceRect, targetRect));

      timeoutRef.current = window.setTimeout(() => setIsFlightComplete(true), TRANSITION_MS + 80);
    };

    rafRef.current = window.requestAnimationFrame(tryStartFlight);

    return () => {
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [hideDestinationTarget, isAppReady, phase]);

  useEffect(() => {
    if (phase !== "flying" || !isFlightComplete || !isAnimationDone) return;

    // The flight has landed and the wiggle is done: swap atomically. The
    // overlay unmounts and the destination logo is revealed in the same paint
    // (see the "done" layout effect), so the two logos never overlap. Fading
    // the overlay out over the revealed destination instead double-draws the
    // anti-aliased edges and reads as a flicker pulse.
    setPhase("done");
  }, [isAnimationDone, isFlightComplete, phase]);

  if (phase === "done") return null;

  // The static logo is only a stand-in for when the animated SVG errored or
  // never loaded; otherwise the animated SVG's final frame is the logo. Once
  // committed (load error, or flight starting before the SVG arrived) it also
  // covers the remaining loading wait instead of a blank center.
  const showFallbackLogo = isFallbackLogoCommitted || (phase !== "loading" && !isAnimatedLogoLoaded);

  return (
    <>
      <HideDestinationLogo />
      <Overlay
        $phase={phase}
        aria-hidden="true"
      >
        <FlightGroup $transform={transform}>
          <AnimatedLogo
            ref={attachAnimatedLogoRef}
            $isDarkTheme={isDarkTheme}
            $isVisible={!showFallbackLogo}
            aria-hidden="true"
            data={animatedLogoSrc}
            tabIndex={-1}
            type="image/svg+xml"
            onLoad={() => {
              isAnimatedLogoLoadedRef.current = true;
              setIsAnimatedLogoLoaded(true);
            }}
          />
          <FlightLogoFrame>
            <FlightLogo
              ref={logoRef}
              $isVisible={showFallbackLogo}
            />
          </FlightLogoFrame>
        </FlightGroup>
      </Overlay>
    </>
  );
}

export default AppLoadingLogoTransition;
